import React, { useContext, useEffect, useState } from 'react';
import {
  params,
  string,
  shape,
  useDataModel,
  extend,
  QueryContext,
  customType,
} from '@thd-nucleus/data-sources';
import { useParams, useLocation } from '@thd-olt-component-react/router';
import { LayoutGroup, LayoutManager } from '@thd-olt-component-react/layout-manager';
import {
  RecommendationsPlaceholder,
  RecommendationsProvider,
} from '@thd-olt-component-react/recommendations';
import { Heading } from '@thd-olt-component-react/heading';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { EtchWidgets } from '@thd-olt-component-react/etch-widgets';
import { Metadata, ContentMetadata } from '@thd-olt-component-react/metadata';
import { Row, Col } from '@thd-olt-component-react/grid';
import { Hydrator, declareContexts } from '@thd-olt-component-react/hydrator';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { ContentRedirector } from '@thd-nucleus/app-render';
import { SeoLinksLoad, SeoLinks, SeoLinksModel } from './dynamic-imports';

export const Content = (props) => {
  declareContexts([QueryContext, ExperienceContext]);
  const normalizeBreadcrumb = (label) => {
    return [
      {
        dimensionName: 'Content',
        label,
        url: `/c/${label}`,
      },
    ];
  };
  const ctx = useContext(ExperienceContext);
  const { name } = useParams();

  const { data, loading, error } = useDataModel('content', {
    variables: {
      name,
      channel: ctx?.channel?.toUpperCase() || 'DESKTOP',
    },
  });

  const { content } = data || {};
  const { breadcrumbs = [], metadata = {} } = content || {};
  const { heading } = metadata;

  const { search: querystring, pathname } = useLocation();

  const HYDRATION_STANDARD_SCROLL_BUFFER = 750;

  const [recommendationsState, setRecommendationState] = useState({
    ready: false,
    schema: {
      hp_sponsored: false,
      rv_homepage_rr: false,
      con1_home_rr: false,
    },
  });

  const [recxSchema, setrecxSchema] = useState({
    appId: 'HOMEDEPOT01',
    maxProducts: 16,
    products: null,
    showATC: true,
    criteo: {
      version: 'model2',
    },
    certonaSchema: [],
    udsRecsSchema: [],
    event: 'product',
    carouselOptions: {
      fullScrollSkip: 2,
    },
  });

  const makeStickyHeader = () => {
    const el = document.querySelector('.Header3');
    if (el) {
      const headerClass = ['Header3-sticky-desktop', 'Header3__header--slide'];
      el.classList.add(...headerClass);
    }
  };

  const initializeRecommendation = () => {
    const recState = {};
    const certonaList = document.getElementsByClassName('certona-content') || [];
    // eslint-disable-next-line
    for (const schema of certonaList) {
      const { id = '' } = schema || {};
      switch (id) {
      case 'hp_sponsored':
        recxSchema.certonaSchema.push('hp_sponsored');
        recState.hp_sponsored = true;
        break;
      case 'rv_homepage_rr':
        recxSchema.udsRecsSchema.push('rv_homepage_rr');
        recState.rv_homepage_rr = true;
        break;
      case 'con1_home_rr':
        recxSchema.udsRecsSchema.push('con1_home_rr');
        recState.con1_home_rr = true;
        break;
      default:
        break;
      }
    }

    if (certonaList.length) {
      setrecxSchema(recxSchema);
      setRecommendationState({
        ready: true,
        schema: recState,
      });
    }
  };

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('content.page-load', { heading });
    makeStickyHeader();
  }, []);

  useEffect(() => {
    initializeRecommendation();
  }, []);

  return (
    <LayoutManager name="content-wrapper">
      <ContentRedirector name={name} />
      <Row className="content-page grid isBound">
        <Col>
          <Metadata data={metadata}>
            <ContentMetadata contentName={name} />
          </Metadata>
          <LayoutGroup name="breadcrumbs" tag={Row} className="breadcrumbs--scroll">
            <Col flatten>
              <Breadcrumbs
                staticCrumbs={breadcrumbs.length ? breadcrumbs : normalizeBreadcrumb(heading)}
              />
            </Col>
          </LayoutGroup>
          <LayoutGroup name="etch-widget" tag={Row}>
            <Col flatten>
              <Heading title={heading} />
              <RecommendationsProvider resx={recxSchema} ready={recommendationsState.ready}>
                <Row>
                  <EtchWidgets contentName={name} showDpdPod />
                  {recommendationsState.schema.con1_home_rr && (
                    <LayoutGroup tag={Row} name="con1_home_rr">
                      <Col nopadding>
                        <RecommendationsPlaceholder scheme="con1_home_rr" />
                      </Col>
                    </LayoutGroup>
                  )}
                  {recommendationsState.schema.hp_sponsored && (
                    <LayoutGroup tag={Row} name="hp_sponsored">
                      <Col nopadding>
                        <RecommendationsPlaceholder scheme="hp_sponsored" />
                      </Col>
                    </LayoutGroup>
                  )}
                  {recommendationsState.schema.rv_homepage_rr && (
                    <LayoutGroup tag={Row} name="rv_homepage_rr">
                      <Col nopadding>
                        <RecommendationsPlaceholder scheme="rv_homepage_rr" />
                      </Col>
                    </LayoutGroup>
                  )}
                </Row>
              </RecommendationsProvider>
            </Col>
          </LayoutGroup>
        </Col>
      </Row>
      <Hydrator
        id="content-seo-section"
        className="grid"
        waitFor={[SeoLinksLoad]}
        scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
      >
        <LayoutGroup tag={Row} name="seo-content" className="u--paddingTop u--paddingBottom">
          <Col padding="false">
            <SeoLinks path={pathname} />
          </Col>
        </LayoutGroup>
      </Hydrator>
    </LayoutManager>
  );
};

Content.displayName = 'ContentPage';

Content.propTypes = {};

Content.dataModel = extend(
  {
    content: params({
      name: string().isRequired(),
      channel: customType('Channel').enum(['DESKTOP', 'MOBILE', 'TABLET'], 'DESKTOP'),
    }).shape({
      name: string(),
      breadcrumbs: shape({
        dimensionName: string(),
        url: string(),
        label: string(),
      }),
      metadata: shape({
        canonicalURL: string(),
        description: string(),
        fbAdmins: string(),
        heading: string(),
        siteName: string(),
        title: string(),
        type: string(),
      }),
      customerType: string(),
    }),
  },
  RecommendationsPlaceholder,
  EtchWidgets,
  Breadcrumbs,
  SeoLinksModel
);
